import { useRef, useState } from "react";
import {
  Download, Lightbulb, ZoomIn, ZoomOut, MoveHorizontal, MoveVertical, RotateCcw, Trash2, Scissors, Combine,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/hooks/use-toast";

export type MergeMode = "reprocess" | "annex";
export type FitMode = "width" | "height";
export const ZOOM_MIN = 0.5;
export const ZOOM_MAX = 3;
export const ZOOM_STEP = 0.25;

export const clampZoom = (z: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 100) / 100));

const ALLOWED = /\.(pdf|png|jpe?g)$/i;

interface Props {
  onDownload?: () => void;
  showSources?: boolean;
  onToggleSources?: () => void;
  zoom: number;
  onZoom: (z: number) => void;
  fitMode: FitMode;
  onFitMode: (m: FitMode) => void;
  onRotate: () => void;
  attachments?: File[];
  onAttachmentsChange?: (files: File[]) => void;
  onSplit?: () => void;
  onMerge?: (extra: File[], mode: MergeMode) => void;
  onDelete?: () => void;
  mainFile?: File | null;
  deleteLabel?: string;
}

const btn = "inline-flex h-8 w-8 items-center justify-center rounded-md text-foreground/80 transition-colors hover:bg-muted disabled:opacity-40 disabled:pointer-events-none";
const activeBtn = "bg-primary/10 text-primary ring-1 ring-primary/40";

const PdfViewerToolbar = ({
  onDownload, showSources, onToggleSources, zoom, onZoom, fitMode, onFitMode, onRotate,
  attachments = [], onAttachmentsChange, onSplit, onMerge, onDelete, mainFile, deleteLabel = "esta factura",
}: Props) => {
  const mergeRef = useRef<HTMLInputElement>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const modeRef = useRef<MergeMode>("reprocess");

  return (
    <div className="flex items-center gap-0.5 rounded-lg border bg-background/80 px-1 py-0.5" role="toolbar" aria-label="Herramientas del documento">
      <button type="button" className={btn} onClick={onDownload} disabled={!onDownload} title="Descargar documento" aria-label="Descargar documento">
        <Download className="h-4 w-4" />
      </button>
      {onToggleSources && (
        <button type="button" className={`${btn} ${showSources ? activeBtn : ""}`} onClick={onToggleSources} title="Resaltar campos extraídos" aria-label="Resaltar campos" aria-pressed={showSources}>
          <Lightbulb className="h-4 w-4" />
        </button>
      )}
      <button type="button" className={btn} onClick={() => onZoom(clampZoom(zoom + ZOOM_STEP))} disabled={zoom >= ZOOM_MAX} title="Aumentar" aria-label="Aumentar">
        <ZoomIn className="h-4 w-4" />
      </button>
      <button type="button" className={btn} onClick={() => onZoom(clampZoom(zoom - ZOOM_STEP))} disabled={zoom <= ZOOM_MIN} title="Disminuir" aria-label="Disminuir">
        <ZoomOut className="h-4 w-4" />
      </button>
      <button type="button" className={`${btn} ${fitMode === "width" && zoom === 1 ? activeBtn : ""}`} onClick={() => { onFitMode("width"); onZoom(1); }} title="Ajustar al ancho" aria-label="Ajustar al ancho">
        <MoveHorizontal className="h-4 w-4" />
      </button>
      <button type="button" className={`${btn} ${fitMode === "height" && zoom === 1 ? activeBtn : ""}`} onClick={() => { onFitMode("height"); onZoom(1); }} title="Ajustar al alto" aria-label="Ajustar al alto">
        <MoveVertical className="h-4 w-4" />
      </button>
      <button type="button" className={btn} onClick={onRotate} title="Girar 90°" aria-label="Girar">
        <RotateCcw className="h-4 w-4" />
      </button>
      {onSplit && (
        <button type="button" className={btn} onClick={onSplit} title="Separar PDF" aria-label="Separar PDF">
          <Scissors className="h-4 w-4" />
        </button>
      )}
      {onMerge && (
        <>
          <input ref={mergeRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" className="hidden"
            onChange={(e) => {
              const list = Array.from(e.target.files ?? []);
              e.target.value = "";
              const ok = list.filter((f) => ALLOWED.test(f.name));
              if (ok.length < list.length) toast({ title: "Formato no admitido", description: "Solo PDF, PNG o JPEG.", variant: "destructive" });
              if (ok.length) onMerge(ok, modeRef.current);
            }} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className={btn} title="Unir PDF" aria-label="Unir PDF">
                <Combine className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => { modeRef.current = "reprocess"; setTimeout(() => mergeRef.current?.click(), 0); }}>
                Unir y reprocesar con IA
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => { modeRef.current = "annex"; setTimeout(() => mergeRef.current?.click(), 0); }}>
                Añadir como anexo (sin reprocesar)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
      {onDelete && (
        <>
          <button type="button" className={`${btn} hover:bg-destructive/10 hover:text-destructive`} onClick={() => setConfirmOpen(true)} title="Eliminar" aria-label="Eliminar">
            <Trash2 className="h-4 w-4" />
          </button>
          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Eliminar {deleteLabel}?</AlertDialogTitle>
                <AlertDialogDescription>Se quitará del lote actual.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { setConfirmOpen(false); onDelete(); }}>Eliminar</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
};

export default PdfViewerToolbar;
